import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { User, Calendar, CreditCard, CheckCircle, AlertCircle, ExternalLink, Clock, Plus, Trash2, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const UK_REGIONS = [
  "London", "South East", "South West", "East of England", "East Midlands",
  "West Midlands", "Yorkshire and The Humber", "North West", "North East",
  "Scotland", "Wales", "Northern Ireland"
];
const CUISINES = [
  "British", "French", "Italian", "Spanish", "Greek", "Indian", "Japanese", "Chinese",
  "Thai", "Asian Fusion", "Mediterranean", "Modern European", "Middle Eastern",
  "Mexican", "American", "Vegetarian", "Vegan", "Seafood"
];
const SERVICES = [
  "Dinner Parties", "Private Events", "Weddings", "Corporate Events",
  "Weekly Meal Prep", "Holiday Cooking", "Cooking Classes", "Kitchen Residency", "Pop-up Dining"
];

const TABS = [
  { id: "profile", label: "My Profile", icon: User },
  { id: "availability", label: "Availability", icon: Calendar },
  { id: "subscription", label: "Subscription", icon: CreditCard },
];

export default function ChefDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API}${url}`;
  };

  // Profile form fields
  const [bio, setBio] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [cuisines, setCuisines] = useState([]);
  const [services, setServices] = useState([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [regions, setRegions] = useState([]);
  const [specificRegion, setSpecificRegion] = useState("");
  const [availDates, setAvailDates] = useState([]);
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/chef/profile`, { withCredentials: true });
      setProfile(data);
      setBio(data.bio || ""); setPhotoUrl(data.photo_url || ""); setPhone(data.phone || "");
      setWebsite(data.website || ""); setCuisines(data.cuisine_specialties || []);
      setServices(data.service_types || []); setPriceMin(data.price_range_min || "");
      setPriceMax(data.price_range_max || ""); setRegions(data.regions || []);
      setSpecificRegion(data.specific_region || "");
      setAvailDates(data.availability_dates || []);
      setGalleryUrls(data.gallery_urls || []);
    } catch {
      setError("Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true); setError("");
    try {
      await axios.put(`${API}/api/chef/profile`, {
        bio, photo_url: photoUrl, phone, website,
        cuisine_specialties: cuisines, service_types: services,
        price_range_min: priceMin ? parseInt(priceMin) : null,
        price_range_max: priceMax ? parseInt(priceMax) : null,
        regions, specific_region: specificRegion
      }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/api/chef/upload-photo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPhotoUrl(data.photo_url);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setGalleryUploading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/api/chef/gallery/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setGalleryUrls(data.gallery_urls);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Gallery upload failed");
    } finally {
      setGalleryUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const removeGalleryPhoto = async (url) => {
    setError("");
    try {
      const { data } = await axios.delete(`${API}/api/chef/gallery`, {
        params: { url }, withCredentials: true
      });
      setGalleryUrls(data.gallery_urls);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove photo");
    }
  };

  const saveAvailability = async () => {    setSaving(true);
    try {
      await axios.put(`${API}/api/chef/availability`, { availability_dates: availDates }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const startPayment = async () => {
    try {
      const { data } = await axios.post(`${API}/api/payments/checkout`,
        { host_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Payment error");
    }
  };

  const [promoCode, setPromoCode] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);

  const applyPromo = async () => {
    setApplyingPromo(true); setError("");
    try {
      await axios.post(`${API}/api/chef/apply-promo`,
        { code: promoCode.trim() }, { withCredentials: true });
      setPromoCode("");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const toggleItem = (arr, setArr, item) =>
    setArr(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);

  const selectedDateObjects = availDates.map(d => {
    const [y, m, day] = d.split("-");
    return new Date(+y, +m - 1, +day);
  });

  const handleDateSelect = (dates) => {
    setAvailDates(dates ? dates.map(d => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }) : []);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-16">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const isActive = profile?.subscription_status === "active";

  return (
    <div className="min-h-screen bg-[#050505] pt-16">
      {/* Header */}
      <div className="border-b border-[#D4AF37]/15 bg-[#080808] py-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-2">Chef Portal</p>
          <h1 className="font-serif text-4xl text-white font-light mb-1">Welcome, {user?.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            {profile?.approval_status === "pending" ? (
              <span data-testid="pending-approval-badge"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37]">
                <Clock size={12} /> Pending Approval
              </span>
            ) : isActive ? (
              <span data-testid="subscription-active-badge"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-emerald-950/50 border border-emerald-800/50 text-emerald-400">
                <CheckCircle size={12} /> {profile?.is_free_member ? "Free Member" : "Active Listing"}
              </span>
            ) : (
              <span data-testid="subscription-inactive-badge"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-950/50 border border-amber-800/50 text-amber-400">
                <AlertCircle size={12} /> Subscription Required
              </span>
            )}
            {profile && (
              <a href={`/chef/${profile._id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-white transition-colors ml-2">
                <ExternalLink size={12} /> View Public Profile
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex border-b border-[#D4AF37]/15 mb-8">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? "border-[#D4AF37] text-[#D4AF37]"
                    : "border-transparent text-[#A0A0A0] hover:text-white"
                }`}
              >
                <Icon size={15} /> {tab.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-900/30 text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {saved && (
          <div className="mb-6 bg-emerald-900/20 border border-emerald-800/30 text-emerald-400 px-4 py-3 text-sm flex items-center gap-2">
            <CheckCircle size={14} /> Changes saved successfully
          </div>
        )}
        {profile?.approval_status === "pending" && (
          <div data-testid="pending-banner"
            className="mb-6 bg-[#D4AF37]/5 border border-[#D4AF37]/30 px-5 py-4 flex items-start gap-3">
            <Clock size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[#D4AF37] text-sm font-medium mb-1">Profile under review</p>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                Your free membership has been activated, but your listing is not yet visible to clients.
                Our team will review your profile within 48 hours. You'll see this badge update once you've been approved.
              </p>
            </div>
          </div>
        )}
        {profile?.approval_status === "rejected" && (
          <div data-testid="rejected-banner"
            className="mb-6 bg-red-900/10 border border-red-900/40 px-5 py-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-400 text-sm font-medium mb-1">Profile not approved</p>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                Your profile didn't meet our directory standards. Please update your bio, photo and details, then contact us at hello to request a re-review.
              </p>
            </div>
          </div>
        )}

        {/* PROFILE TAB */}
        {activeTab === "profile" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Bio / About You">
                <textarea
                  data-testid="profile-bio-input"
                  value={bio} onChange={e => setBio(e.target.value)}
                  rows={5} placeholder="Tell clients about your experience, style and approach..."
                  className="w-full bg-transparent border border-white/10 focus:border-[#D4AF37] p-3 text-white placeholder-white/25 text-sm outline-none transition-colors resize-none"
                />
              </FormField>
              <div className="space-y-6">
                <FormField label="Profile Photo">
                  <div className="flex gap-4 items-start">
                    <div className="w-20 h-20 border border-[#D4AF37]/20 overflow-hidden flex-shrink-0 bg-[#1A1A1A]">
                      {photoUrl ? (
                        <img src={getPhotoUrl(photoUrl)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User size={24} className="text-[#555]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileUpload} className="hidden" data-testid="photo-file-input"
                      />
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        disabled={uploading} data-testid="upload-photo-btn"
                        className="border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] px-4 py-2 text-sm tracking-wide transition-all duration-200 disabled:opacity-50 block mb-2"
                      >
                        {uploading ? "Uploading..." : "Upload Photo"}
                      </button>
                      <p className="text-[#555] text-xs mb-2">JPG, PNG or WebP. Max 5MB.</p>
                      <input
                        type="url" value={photoUrl.startsWith("/api/") ? "" : photoUrl}
                        onChange={e => setPhotoUrl(e.target.value)}
                        placeholder="Or paste external photo URL..."
                        data-testid="profile-photo-input"
                        className="w-full bg-transparent border-b border-white/10 focus:border-[#D4AF37]/50 py-1.5 text-[#A0A0A0] placeholder-white/15 text-xs outline-none transition-colors"
                      />
                    </div>
                  </div>
                </FormField>
                <FormField label="Phone Number">
                  <input data-testid="profile-phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="+44..." className={inputClass} />
                </FormField>
                <FormField label="Website">
                  <input data-testid="profile-website-input" type="url" value={website} onChange={e => setWebsite(e.target.value)}
                    placeholder="https://..." className={inputClass} />
                </FormField>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Price Range (£ per event)">
                <div className="flex gap-3 items-center">
                  <input data-testid="profile-price-min" type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)}
                    placeholder="Min" className={`${inputClass} flex-1`} min="0" />
                  <span className="text-[#A0A0A0]">—</span>
                  <input data-testid="profile-price-max" type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)}
                    placeholder="Max" className={`${inputClass} flex-1`} min="0" />
                </div>
              </FormField>
              <FormField label="Specific Area / Town">
                <input data-testid="profile-specific-region" type="text" value={specificRegion}
                  onChange={e => setSpecificRegion(e.target.value)}
                  placeholder="e.g. Mayfair, Manchester City Centre..." className={inputClass} />
              </FormField>
            </div>

            <CheckboxGroup label="Cuisine Specialties" items={CUISINES} selected={cuisines}
              onToggle={item => toggleItem(cuisines, setCuisines, item)} testPrefix="cuisine" />
            <CheckboxGroup label="Services Offered" items={SERVICES} selected={services}
              onToggle={item => toggleItem(services, setServices, item)} testPrefix="service" />
            <CheckboxGroup label="UK Regions Covered" items={UK_REGIONS} selected={regions}
              onToggle={item => toggleItem(regions, setRegions, item)} testPrefix="region" />

            {/* GALLERY */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
                  <ImageIcon size={12} className="inline mr-2" />
                  Photo Gallery <span className="text-[#555] normal-case tracking-normal">({galleryUrls.length}/5)</span>
                </p>
                <input
                  type="file" ref={galleryInputRef} accept="image/jpeg,image/png,image/webp"
                  onChange={handleGalleryUpload} className="hidden" data-testid="gallery-file-input"
                />
                <button
                  type="button"
                  data-testid="add-gallery-btn"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={galleryUploading || galleryUrls.length >= 5}
                  className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] px-4 py-2 text-xs tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={13} /> {galleryUploading ? "Uploading..." : "Add Photo"}
                </button>
              </div>
              <p className="text-[#555] text-xs mb-3">Up to 5 photos showcasing your dishes and events. JPG, PNG or WebP. Max 5MB each.</p>
              {galleryUrls.length === 0 ? (
                <div className="border border-dashed border-white/10 px-6 py-10 text-center">
                  <p className="text-[#A0A0A0] text-sm">No gallery photos yet — add up to 5 to showcase your work.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {galleryUrls.map(url => (
                    <div key={url} data-testid="gallery-thumb" className="relative group aspect-square overflow-hidden bg-[#0A0A0A] border border-[#D4AF37]/15">
                      <img src={getPhotoUrl(url)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-gallery-btn`}
                        onClick={() => removeGalleryPhoto(url)}
                        className="absolute top-1 right-1 bg-black/80 text-white hover:bg-red-600 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="save-profile-btn"
              onClick={saveProfile} disabled={saving}
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-10 py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-200 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        )}

        {/* AVAILABILITY TAB */}
        {activeTab === "availability" && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 p-6">
              <p className="text-[#A0A0A0] text-sm mb-6">
                Select the dates you are available for bookings. Clients can filter chefs by specific dates.
              </p>
              <div className="flex justify-center mb-6">
                <DayPicker
                  mode="multiple"
                  selected={selectedDateObjects}
                  onSelect={handleDateSelect}
                  fromDate={new Date()}
                  className="text-white"
                />
              </div>
              <div className="border-t border-white/5 pt-4">
                <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] mb-3">
                  Selected Dates ({availDates.length})
                </p>
                {availDates.length === 0 ? (
                  <p className="text-[#A0A0A0] text-sm">No dates selected</p>
                ) : (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {availDates.sort().map(d => (
                      <span key={d} className="text-xs px-3 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37] font-mono">{d}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              data-testid="save-availability-btn"
              onClick={saveAvailability} disabled={saving}
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-10 py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-200 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Availability"}
            </button>
          </div>
        )}

        {/* SUBSCRIPTION TAB */}
        {activeTab === "subscription" && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-8">
              <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-6">Listing Subscription</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-3 h-3 rounded-full ${isActive ? "bg-emerald-400" : "bg-amber-400"}`} />
                    <span className="text-white text-lg font-medium capitalize">
                      {profile?.is_free_member ? "Free Member — Active" : (profile?.subscription_status || "inactive")}
                    </span>
                  </div>
                  {profile?.is_free_member ? (
                    <p data-testid="free-member-status" className="text-[#A0A0A0] text-sm mb-2">
                      No expiry — your listing remains active until revoked.
                    </p>
                  ) : isActive && profile?.subscription_expires && (
                    <p className="text-[#A0A0A0] text-sm mb-2">
                      Expires: {new Date(profile.subscription_expires).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  )}
                  {isActive ? (
                    <p className="text-[#A0A0A0] text-sm leading-relaxed">
                      Your profile is active and visible to clients searching the directory.
                    </p>
                  ) : (
                    <p className="text-[#A0A0A0] text-sm leading-relaxed">
                      Your profile is not yet visible in the directory. Complete your subscription to activate your listing.
                    </p>
                  )}
                </div>
                <div className="border border-[#D4AF37]/20 p-6">
                  <div className="font-serif text-5xl text-[#D4AF37] mb-1">£40</div>
                  <p className="text-[#A0A0A0] text-sm mb-1">per quarter (every 3 months)</p>
                  <p className="text-[#555] text-xs mb-6">Secure payment via Stripe</p>
                  <ul className="space-y-2 mb-6">
                    {["Visible in chef directory", "Searchable by region, cuisine & date", "Direct client enquiries", "Manage your availability"].map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                        <CheckCircle size={13} className="text-[#D4AF37] flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid="pay-subscription-btn"
                    onClick={startPayment}
                    disabled={profile?.is_free_member}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {profile?.is_free_member ? "Free Membership Active" : (isActive ? "Renew Subscription" : "Activate Listing — £40")}
                  </button>
                </div>
              </div>
            </div>

            {!profile?.is_free_member && (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 p-8">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">Have a Promo Code?</p>
                <p className="text-[#A0A0A0] text-sm mb-4 leading-relaxed">
                  Redeem a promo code for free lifetime membership (no expiry until revoked).
                </p>
                <div className="flex gap-3 max-w-lg">
                  <input
                    data-testid="promo-code-input"
                    type="text"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="flex-1 bg-transparent border border-white/15 focus:border-[#D4AF37] px-4 py-3 text-white placeholder-white/25 text-sm outline-none transition-colors font-mono tracking-wider"
                  />
                  <button
                    data-testid="apply-promo-btn"
                    onClick={applyPromo}
                    disabled={applyingPromo || !promoCode.trim()}
                    className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-6 py-3 text-sm tracking-widest uppercase font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {applyingPromo ? "Applying..." : "Apply"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputClass = "w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white placeholder-white/25 text-sm outline-none transition-colors";

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">{label}</label>
      {children}
    </div>
  );
}

function CheckboxGroup({ label, items, selected, onToggle, testPrefix }) {
  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(item => (
          <button
            key={item}
            type="button"
            data-testid={`${testPrefix}-${item.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onToggle(item)}
            className={`px-3 py-2 text-sm border transition-all duration-150 ${
              selected.includes(item)
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 text-[#A0A0A0] hover:border-[#D4AF37]/40 hover:text-white"
            }`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}
